import { useCallback, useEffect, useState } from 'react'
import { Cpu, Database, HardDrive, Network, RefreshCw, Server, ShieldCheck, ShieldX, Timer, Wifi, WifiOff } from 'lucide-react'
import { getBookLogo } from '../lib/logoMaps'
import { fetchReceiverHealth, fetchReceiverStats } from '../lib/api'
import './OracleVMHealthWidget.css'

const VM_IP = '170.9.62.134'
const VM_PORT = 8443

const BOOKMAKER_LABELS = {
  ballybet: 'Bally Bet',
  betmgm: 'BetMGM',
  betonline: 'BetOnline',
  betrivers: 'BetRivers',
  bovada: 'Bovada',
  caesars: 'Caesars',
  draftkings: 'DraftKings',
  fanatics: 'Fanatics',
  fanduel: 'FanDuel',
  fliff: 'Fliff',
  hardrock: 'Hard Rock',
  kalshi: 'Kalshi',
  mybookie: 'MyBookie',
  pinnacle: 'Pinnacle',
  polymarket: 'Polymarket',
  thescore: 'TheScore',
}

const LEAGUE_LABELS = {
  nfl: 'NFL',
  nba: 'NBA',
  mlb: 'MLB',
  nhl: 'NHL',
  cfb: 'CFB',
  ncaaf: 'NCAAF',
  cbb: 'CBB',
  ncaab: 'NCAAB',
  soccer: 'Soccer',
  mma: 'MMA',
  tennis: 'Tennis',
}

function formatUptime(totalSeconds) {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '--'

  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)

  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

function formatBookmakerLabel(rawKey) {
  const normalized = String(rawKey || '')
    .toLowerCase()
    .replace(/^abet_scraper_/, '')
    .replace(/[_-]+/g, '')

  return BOOKMAKER_LABELS[normalized] || String(rawKey || '').replace(/[_-]+/g, ' ')
}

function formatLeagueLabel(rawKey) {
  const normalized = String(rawKey || '').toLowerCase()
  return LEAGUE_LABELS[normalized] || normalized.toUpperCase() || '--'
}

function toSortedEntries(map) {
  return Object.entries(map || {})
    .map(([key, value]) => [key, Number(value) || 0])
    .sort((a, b) => b[1] - a[1])
}

function getBookLogoKey(rawKey) {
  return String(rawKey || '').replace(/^abet_scraper_/, '').replace(/[_-]+/g, ' ')
}

function countParsers(parsers) {
  if (Array.isArray(parsers)) return parsers.length
  if (parsers && typeof parsers === 'object') return Object.keys(parsers).length
  if (typeof parsers === 'number') return parsers
  return 0
}

export default function OracleVMHealthWidget() {
  const [health, setHealth] = useState(null)
  const [stats, setStats] = useState(null)
  const [status, setStatus] = useState('offline')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lastCheck, setLastCheck] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)

    const [healthResult, statsResult] = await Promise.allSettled([
      fetchReceiverHealth(),
      fetchReceiverStats(),
    ])

    const nextHealth = healthResult.status === 'fulfilled' && healthResult.value?.status === 'ok'
      ? healthResult.value.data
      : null
    const nextStats = statsResult.status === 'fulfilled' && statsResult.value?.status === 'ok'
      ? statsResult.value.data
      : null
    const healthError = healthResult.status === 'rejected'
      ? healthResult.reason?.message
      : healthResult.value?.message
    const statsError = statsResult.status === 'rejected'
      ? statsResult.reason?.message
      : statsResult.value?.message

    setHealth(nextHealth)
    setStats(nextStats)

    if (nextHealth && nextStats) {
      setStatus('online')
      setError(null)
    } else if (nextHealth || nextStats) {
      setStatus('warning')
      setError(healthError || statsError || 'Receiver responded with partial data')
    } else {
      setStatus('offline')
      setError(healthError || statsError || 'Oracle VM is unreachable')
    }

    setLastCheck(new Date().toLocaleTimeString())
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 30000)
    return () => clearInterval(interval)
  }, [fetchData])

  const bookmakerRows = toSortedEntries(stats?.by_bookmaker)
  const leagueRows = toSortedEntries(stats?.by_league)
  const totalOdds = stats?.total_odds ?? health?.odds_count ?? bookmakerRows.reduce((sum, [, count]) => sum + count, 0)
  const parsersCount = countParsers(health?.parsers)
  const cardStatus = status === 'online' ? 'online' : status === 'warning' ? 'warning' : 'error'
  const badgeStatus = status === 'online' ? 'online' : status === 'warning' ? 'warning' : 'error'
  const receiverStatusLabel = String(health?.status || (status === 'online' ? 'ok' : 'offline')).toUpperCase()

  return (
    <div className={`widget-card ovm-widget ${cardStatus}`}>
      <div className="widget-header">
        {status === 'online' ? <Wifi size={16} className="widget-icon" /> : <WifiOff size={16} className="widget-icon" />}
        <span className="widget-title">Oracle VM Health</span>
        <span className="ovm-endpoint">{VM_IP}:{VM_PORT}</span>
        <span className={`status-badge ${badgeStatus}`}>
          {status === 'online' ? 'ONLINE' : status === 'warning' ? 'DEGRADED' : 'OFFLINE'}
        </span>
        <button className={`refresh-btn ${loading ? 'spinning' : ''}`} onClick={fetchData} disabled={loading}>
          <RefreshCw size={12} />
        </button>
      </div>

      <div className="widget-body">
        {error && <div className="widget-error">{error}</div>}

        {loading && !health && !stats && (
          <div className="widget-empty">Loading Oracle VM health...</div>
        )}

        {!loading && !health && !stats && (
          <div className="widget-empty ovm-offline">
            <WifiOff size={24} />
            <div className="ovm-offline-title">Receiver offline</div>
            <div className="ovm-offline-text">No response from {VM_IP}:{VM_PORT}</div>
          </div>
        )}

        {(health || stats) && (
          <>
            <div className="ovm-summary-grid">
              <div className="ovm-stat-card">
                <div className="ovm-stat-icon"><Timer size={14} /></div>
                <div className="ovm-stat-value">{formatUptime(health?.uptime_seconds)}</div>
                <div className="ovm-stat-label">Uptime</div>
              </div>
              <div className="ovm-stat-card">
                <div className="ovm-stat-icon"><Database size={14} /></div>
                <div className="ovm-stat-value">{Number(totalOdds || 0).toLocaleString()}</div>
                <div className="ovm-stat-label">Total Odds</div>
              </div>
              <div className="ovm-stat-card">
                <div className="ovm-stat-icon"><Network size={14} /></div>
                <div className="ovm-stat-value">{parsersCount || '--'}</div>
                <div className="ovm-stat-label">Parsers</div>
              </div>
              <div className="ovm-stat-card">
                <div className="ovm-stat-icon"><Server size={14} /></div>
                <div className="ovm-stat-value">{health?.version || '--'}</div>
                <div className="ovm-stat-label">Version</div>
              </div>
            </div>

            <div className="ovm-meta-grid">
              <div className="ovm-meta-card">
                <span className="ovm-meta-label">Receiver</span>
                <span className={`ovm-meta-pill ${status === 'online' ? 'online' : status === 'warning' ? 'warning' : 'offline'}`}>
                  {receiverStatusLabel}
                </span>
              </div>
              <div className="ovm-meta-card">
                <span className="ovm-meta-label">Supabase</span>
                <span className={`ovm-meta-pill ${health?.supabase_configured ? 'online' : 'offline'}`}>
                  {health?.supabase_configured ? <ShieldCheck size={12} /> : <ShieldX size={12} />}
                  {health?.supabase_configured ? 'Configured' : 'Missing'}
                </span>
              </div>
              <div className="ovm-meta-card">
                <span className="ovm-meta-label">Health Count</span>
                <span className="ovm-meta-value">{Number(health?.odds_count || 0).toLocaleString()}</span>
              </div>
              <div className="ovm-meta-card">
                <span className="ovm-meta-label">Stats Count</span>
                <span className="ovm-meta-value">{Number(stats?.total_odds || 0).toLocaleString()}</span>
              </div>
            </div>

            <div className="ovm-panels">
              <section className="ovm-panel">
                <div className="ovm-panel-header">
                  <span>By Bookmaker</span>
                  <span className="ovm-panel-count">{bookmakerRows.length}</span>
                </div>
                {bookmakerRows.length === 0 ? (
                  <div className="ovm-panel-empty">No bookmaker stats yet</div>
                ) : (
                  <div className="ovm-table">
                    {bookmakerRows.map(([book, count]) => (
                      <div key={book} className="ovm-table-row">
                        <div className="ovm-table-name">
                          {getBookLogo(getBookLogoKey(book)) && (
                            <img
                              src={getBookLogo(getBookLogoKey(book))}
                              alt=""
                              className="ovm-book-logo"
                              onError={(event) => { event.target.style.display = 'none' }}
                            />
                          )}
                          <span>{formatBookmakerLabel(book)}</span>
                        </div>
                        <span className="ovm-table-value">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="ovm-panel">
                <div className="ovm-panel-header">
                  <span>By League</span>
                  <span className="ovm-panel-count">{leagueRows.length}</span>
                </div>
                {leagueRows.length === 0 ? (
                  <div className="ovm-panel-empty">No league stats yet</div>
                ) : (
                  <div className="ovm-table">
                    {leagueRows.map(([league, count]) => (
                      <div key={league} className="ovm-table-row">
                        <div className="ovm-table-name">
                          <span className="ovm-league-pill">{formatLeagueLabel(league)}</span>
                        </div>
                        <span className="ovm-table-value">{count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>

      <div className="ovm-vm-info">
        <div className="ovm-vm-info-row"><HardDrive size={12} /><span>Oracle Cloud AMD Micro</span><span className="ovm-vm-info-detail">1 OCPU · 1GB RAM</span></div>
        <div className="ovm-vm-info-row"><Cpu size={12} /><span>x86_64 Ubuntu 24.04</span><span className="ovm-vm-info-detail">US West (San Jose)</span></div>
      </div>

      <div className="widget-footer">
        <span>{lastCheck ? `Checked: ${lastCheck}` : 'Loading...'}</span>
        <span>{status === 'online' ? 'Polling every 30s' : 'Retrying every 30s'}</span>
      </div>
    </div>
  )
}
